'use client';

import { motion } from 'framer-motion';
import Image from 'next/image';
import React, { Fragment } from 'react';

import { openGameProvider } from '@/components/core/OpenGame';
import { useAppRouter } from '@/components/hooks/useAppRouter';
import useClientTranslation from '@/components/hooks/useClientTranslation';
import MatchCard from '@/components/modules/Slider/MatchSlider/components/MatchCard';
import TopSlider from '@/components/modules/Slider/TopSlider';

import { PARTNER_PROVIDER } from '@/constant/data';

interface ISportProps {
  topMatch?: any[] | [];
  args?: {
    display?: React.Key;
    limit?: React.Key;
  };
}

const container = {
  hidden: { opacity: 1, scale: 0 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.5,
      delayChildren: 0.3,
      staggerChildren: 0.2,
    },
  },
};

const items = {
  hidden: { rotateY: 180, opacity: 0, scale: 0.5 },
  visible: {
    y: 0,
    scale: 1,
    opacity: 1,
    rotateY: 0,
    transition: {
      duration: 0.3,
    },
  },
};

const sportBookData = [
  // TODO: TBU
  // {
  //   provider: PARTNER_PROVIDER.sbo,
  //   title: 'SBO',
  //   banner: '/img/game/sport/content_5.png',
  //   link: '',
  //   logoProvider: '/img/game/sport/sbo.png',
  //   buttonColor: '#0A86CC',
  //   textBtn: 'mainSlider.betBtn',
  //   params: {
  //     partner_provider: PARTNER_PROVIDER.sbo,
  //     category_name: 'SportsBook',
  //     odd_style: 'MY',
  //     odds_mode: 'double',
  //     is_new_tab: false,
  //     // odd_style: MY/HK/EU/ID
  //     // odds_mode: double/single
  //     is_require_login: true,
  //   },
  // },
  // {
  //   provider: PARTNER_PROVIDER.athena,
  //   title: 'SABA',
  //   banner: '/img/game/sport/content_1.png',
  //   link: '',
  //   logoProvider: '/img/game/sport/saba.png',
  //   buttonColor: '#D3A300',
  //   textBtn: 'mainSlider.betBtn',
  //   params: {
  //     partner_provider: PARTNER_PROVIDER.athena,
  //     is_new_tab: false,
  //   },
  // },
  {
    provider: PARTNER_PROVIDER.ksport,
    title: 'K-Sport',
    banner: '/img/game/sport/content_2.png',
    link: '',
    logoProvider: '/img/game/sport/ksport.png',
    buttonColor: '#E83A64',
    textBtn: 'mainSlider.betBtn',
    params: {
      partner_provider: PARTNER_PROVIDER.ksport,
      is_new_tab: true,
    },
    detail: {
      partner_provider: PARTNER_PROVIDER.ksport,
      is_new_tab: true,
    },
  },
  // {
  //   provider: PARTNER_PROVIDER.bti,
  //   title: 'BTI',
  //   banner: '/img/game/sport/content_3.png',
  //   link: '',
  //   logoProvider: '/img/game/sport/ksport.png',
  //   buttonColor: '#0EA968',
  //   textBtn: 'mainSlider.betBtn',
  //   params: {
  //     partner_provider: PARTNER_PROVIDER.bti,
  //     is_new_tab: false,
  //   },
  // },
];

const Sport: React.FC<ISportProps> = ({ topMatch = [] }) => {
  const { router } = useAppRouter();
  const { t } = useClientTranslation('HomePage');
  return (
    <div className='flex flex-col'>
      <TopSlider
        marqueeContainerClass='w-full !pr-0'
        standClass='hidden'
      ></TopSlider>
      <div className='absolute left-0 right-0 top-0 h-full w-full overflow-hidden bg-[#5b8ab6]'>
        <img
          className='absolute left-0 right-0 top-0 h-full w-full scale-[1.025]  blur-[5px]'
          width={1440}
          height={768}
          src='/img/game/sport/background.png'
          alt='bg'
        />
      </div>
      <motion.div
        className='relative flex justify-center gap-[58.33px] pt-[60px]'
        variants={container}
        initial='hidden'
        animate='visible'
      >
        {sportBookData.map((item, index) => {
          return (
            <motion.div
              variants={items}
              key={item.provider}
              onClick={() => openGameProvider(item.params, router)}
              className='flex flex-col items-center'
            >
              <div className='relative transition-all hover:!scale-[1.1]'>
                <div className='mx-auto pb-[24.99px] text-center text-[36px] font-bold uppercase text-white'>
                  {item.title}
                </div>
                <div className='relative'>
                  {index === 0 && (
                    <img
                      className='absolute right-[27px] top-[7px] w-[30px]'
                      src='/img/common/hot_badge_2.png'
                      alt=''
                    />
                  )}
                  <Image
                    src={item.banner}
                    width={192}
                    height={276}
                    alt='banner'
                    className='cursor-hand'
                  />

                  <img
                    className='absolute bottom-8 left-1/2 w-[60px] -translate-x-1/2'
                    src={item.logoProvider}
                    alt=''
                  />
                </div>
                <div className='mx-auto pt-3 text-center'>
                  <div className='relative mx-auto flex h-[54px] w-[149px] items-center justify-center'>
                    <div className='cursor-hand absolute -right-[12px] top-0 -scale-x-100'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='32'
                        height='55'
                        viewBox='0 0 32 55'
                        fill='none'
                      >
                        <path
                          d='M25.5 -3.99994L4.99951 27.3797L28 61.5001'
                          stroke={item.buttonColor}
                          strokeOpacity='0.5'
                          strokeWidth='8'
                        />
                      </svg>
                    </div>
                    <div className='cursor-hand absolute -right-[21px] top-0 -scale-x-100 animate-pulse'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='30'
                        height='55'
                        viewBox='0 0 30 55'
                        fill='none'
                      >
                        <path
                          d='M24.5 -3.99994L3.99951 27.3797L27 61.5001'
                          stroke={item.buttonColor}
                          strokeOpacity='0.3'
                          strokeWidth='6'
                        />
                      </svg>
                    </div>
                    <div className='cursor-hand absolute left-0 top-0'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='150'
                        height='55'
                        viewBox='0 0 150 55'
                        fill='none'
                      >
                        <path
                          d='M16.7451 1.29914C17.113 0.726148 17.7472 0.3797 18.4281 0.3797H131.199C131.873 0.3797 132.502 0.718977 132.871 1.28228L149.28 26.2823C149.717 26.9485 149.717 27.8109 149.28 28.4771L132.871 53.4771C132.502 54.0404 131.873 54.3797 131.199 54.3797H19.3949C18.7322 54.3797 18.1126 54.0515 17.7403 53.5033L0.742037 28.4724C0.291988 27.8097 0.280807 26.9423 0.713618 26.2682L16.7451 1.29914Z'
                          fill={item.buttonColor}
                        />
                      </svg>
                    </div>
                    <div className='cursor-hand absolute -left-[11px] top-0'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='32'
                        height='55'
                        viewBox='0 0 32 55'
                        fill='none'
                      >
                        <path
                          d='M25.5 -3.99994L4.99951 27.3797L28 61.5001'
                          stroke={item.buttonColor}
                          strokeOpacity='0.5'
                          strokeWidth='8'
                        />
                      </svg>
                    </div>
                    <div className='cursor-hand absolute -left-[20px] top-0 animate-pulse'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='30'
                        height='55'
                        viewBox='0 0 30 55'
                        fill='none'
                      >
                        <path
                          d='M24.5 -3.99994L3.99951 27.3797L27 61.5001'
                          stroke={item.buttonColor}
                          strokeOpacity='0.3'
                          strokeWidth='6'
                        />
                      </svg>
                    </div>
                    <div className='cursor-hand text-24 relative font-bold text-white'>
                      {t(item.textBtn)}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </motion.div>
      <div className='container-pc relative !mt-[60px] flex flex-wrap justify-center gap-3 pb-[87px]'>
        {topMatch.map((_, index) => {
          if (!_) return null;
          return (
            <Fragment key={index}>
              <MatchCard
                className='!m-0 !h-[169px] !min-h-[183px] !w-[392px] !min-w-[392px]'
                cardItem={_}
              />
            </Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default Sport;
